import { usePublishEvent } from '@agilelab/plugin-wb-events';
import { marketplaceHomeSearchedTopic } from '@agilelab/plugin-wb-events-common';
import { entityRefToUrn } from '@agilelab/plugin-wb-builder-common';
import { configurationApiRef } from '@agilelab/plugin-wb-configuration-common';
import { metricsApiRef } from '@agilelab/plugin-wb-metrics';
import {
  HotelClassIcon,
  Logo,
  useEnabledFeatures,
  useEnvironmentsContext,
  useResponsiveMobile,
  WbCard,
  WbDivider,
  WbSupportButton,
} from '@agilelab/plugin-wb-platform';
import { WittyAssistant } from '@agilelab/plugin-wb-witty-react';
import {
  ALL_TAXONOMIES_FILTER,
  practiceShaperApiRef,
  useTaxonomySelection,
} from '@agilelab/plugin-wb-practice-shaper';
import {
  MARKETPLACE_PROJECTS_DATA_LANDSCAPE,
  MARKETPLACE_PROJECTS_URN,
  WitboostMarketplaceSearchResultSet,
} from '@agilelab/plugin-wb-search-common';
import { marketplaceApiRef } from '../../../plugin';
import {
  filterValidMarketplaceUrns,
  getSelectedSearchResults,
} from '../../../utils/marketplaceSearch';
import { useApolloClient } from '@apollo/client';
import { ErrorPanel } from '@mainstage/core-components';
import {
  configApiRef,
  identityApiRef,
  useApi,
  useRouteRef,
} from '@mainstage/core-plugin-api';
import { useStarredEntities } from '@mainstage/plugin-catalog-react';
import { Box, makeStyles, Typography, useTheme } from '@material-ui/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { createSearchParams } from 'react-router-dom';
import useAsync from 'react-use/lib/useAsync';
import { marketplaceSearchRef } from '../../../routes';
import { Loader } from '../CustomViews/Loader';
import { MarketplaceSearchDocumentEnrichedResultSet } from '../types';
import { enrichSearchResults, getTopReportIDs } from '../utils';
import { HomepageEmptyState } from './EmptyStates/HomepageEmptyState';
import { SearchFavoriteListItem } from './SearchFavoriteListItem';
import { SearchPageAutocomplete } from './SearchPageAutocomplete';
import BarChartIcon from '@material-ui/icons/BarChart';
import HistoryIcon from '@material-ui/icons/History';
import { usePreferredSearchMode } from '../../MarketplaceDiscoveryPage/hooks/usePreferredSearchMode';
import { useMarketplaceSemanticAvailability } from '../../MarketplaceDiscoveryPage/hooks/useMarketplaceSemanticAvailability';

const useStyles = makeStyles(
  theme => ({
    root: {
      backgroundColor: theme.palette.white,
    },
    container: {
      marginTop: '10vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      rowGap: theme.spacing(1),
    },
    logoContainer: {
      width: '200px',
    },
    form: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
    },
    gridContainer: {
      marginTop: '8vh',
      display: 'grid',
      gridTemplateColumns: '32% 32% 32%',
      width: '100%',
      padding: '2rem',
      columnGap: '2%',
      [theme.breakpoints.down('md')]: {
        padding: '1rem',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '100%',
        rowGap: '2rem',
      },
    },
    title: {
      marginBottom: '10px',
      fontSize: '2.2rem',
      fontWeight: 400,
    },
    widget: {
      width: '100%',
    },
  }),
  { name: 'WbSearchContent' },
);

export const SearchPageContent = () => {
  const [query, setQuery] = useState('');
  const [preferredMode, setMode] = usePreferredSearchMode();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useResponsiveMobile();
  const { data: features } = useEnabledFeatures();
  const { semanticAvailable, semanticLicenseLoading } =
    useMarketplaceSemanticAvailability();
  const mode = semanticAvailable ? preferredMode : 'keyword';
  const activeQuery =
    !semanticAvailable && preferredMode === 'semantic' ? '' : query;

  useEffect(() => {
    if (
      !semanticLicenseLoading &&
      !semanticAvailable &&
      preferredMode === 'semantic'
    ) {
      setQuery('');
      setMode('keyword');
    }
  }, [semanticLicenseLoading, semanticAvailable, preferredMode, setMode]);

  const { starredEntities } = useStarredEntities();
  const favorites = useMemo(
    () =>
      [...starredEntities]
        .filter(entry => entry.startsWith('search:'))
        .map(entry => entry.slice('search:'.length)),
    [starredEntities],
  );
  const queryClient = useQueryClient();
  const wbConfigApi = useApi(configurationApiRef);
  const configApi = useApi(configApiRef);
  const showDemoBadge =
    configApi.getOptionalBoolean('mesh.logo.showDemoBadge') ?? false;
  const navigate = useNavigate();
  const marketplaceSearch = useRouteRef(marketplaceSearchRef);

  const marketplaceApi = useApi(marketplaceApiRef);
  const metricsApi = useApi(metricsApiRef);
  const identityApi = useApi(identityApiRef);
  const apolloClient = useApolloClient();
  const practiceShaperApi = useApi(practiceShaperApiRef);
  const { priorityEnvironment, setEnvironment } = useEnvironmentsContext();
  const { selectedTaxonomyRef } = useTaxonomySelection();
  const { publish } = usePublishEvent({ generatorSystem: 'marketplace' });

  const searchInputId = 'marketplace-search';

  const handleSubmit = (event: any, search?: string) => {
    if (event) event.preventDefault();
    const q = search ?? activeQuery;
    if (mode === 'semantic' && (!semanticAvailable || !q.trim())) return;
    setEnvironment(priorityEnvironment);
    publish(marketplaceHomeSearchedTopic, {
      mode,
      filters: {
        selectedTaxonomy: selectedTaxonomyRef,
        query: q,
      },
    });
    navigate({
      pathname: marketplaceSearch(),
      search: createSearchParams({
        mode,
        ...(q.length ? { query: q } : {}),
      }).toString(),
    });
  };
  const metrics = useAsync(async () => {
    const getMetrics = async () => {
      const credentials = await identityApi.getCredentials();
      const result = await metricsApi.getReportDetails(
        { report_manager: 'UserStatsReportManager', group_by: 'user' },
        credentials,
      );
      return getTopReportIDs(result);
    };
    return getMetrics();
  }, [identityApi, metricsApi]);
  const { topByCount, topByTimestamp } = metrics.value ?? {};
  const enrich = async (resultSet: WitboostMarketplaceSearchResultSet) => {
    const enriched = await enrichSearchResults(
      resultSet.results,
      apolloClient,
      practiceShaperApi,
      queryClient,
      wbConfigApi,
    );
    return enriched;
  };
  const identities = filterValidMarketplaceUrns([
    ...new Set([
      ...favorites,
      ...(topByCount ?? []),
      ...(topByTimestamp ?? []),
    ]),
  ]);
  const savedResults = useQuery<MarketplaceSearchDocumentEnrichedResultSet>({
    queryKey: [
      'marketplace-home-saved',
      priorityEnvironment.name,
      selectedTaxonomyRef,
      identities,
    ],
    queryFn: async ({ signal }) => {
      if (!identities.length) return { results: [], numberOfResults: 0 };
      const page = await getSelectedSearchResults(
        marketplaceApi,
        {
          mode: 'keyword',
          category: 'selected',
          environment: priorityEnvironment.name,
          query: '',
          limit: 50,
          filters: [
            {
              id: MARKETPLACE_PROJECTS_URN.field,
              operator: 'in',
              values: identities,
            },
            ...(selectedTaxonomyRef === ALL_TAXONOMIES_FILTER
              ? []
              : [
                  {
                    id: MARKETPLACE_PROJECTS_DATA_LANDSCAPE.field,
                    operator: 'in' as const,
                    values: [entityRefToUrn(selectedTaxonomyRef)],
                  },
                ]),
          ],
        },
        { signal, allPages: true },
      );
      const results = await enrich(page);
      signal?.throwIfAborted();
      return { ...page, results };
    },
  });
  const result = {
    value: savedResults.data,
    loading: savedResults.isLoading,
    error: savedResults.error,
  };

  const loading = result.loading || metrics.loading;
  const recentlyVisited = useMemo(() => {
    if (loading)
      return (
        <>
          <Loader />
          <Loader />
        </>
      );
    return result?.value?.results && topByTimestamp?.length ? (
      (() => {
        const filteredItems = topByTimestamp
          .map(el => {
            const entity = result?.value?.results.find(
              e => e.document.id === el,
            );
            return entity ? (
              <SearchFavoriteListItem key={el} entity={entity} />
            ) : null;
          })
          .filter(Boolean);

        return filteredItems.length > 0 ? (
          filteredItems
            .filter(el => el !== null)
            .slice(0, 5)
            .map((el, i) => {
              return (
                el && (
                  <Fragment key={`${el.key}-recent`}>
                    {el}
                    {i < filteredItems.length - 1 && i < 4 && (
                      <WbDivider orientation="horizontal" />
                    )}
                  </Fragment>
                )
              );
            })
        ) : (
          <HomepageEmptyState kind="recent" />
        );
      })()
    ) : (
      <HomepageEmptyState kind="recent" />
    );
  }, [loading, result?.value?.results, topByTimestamp]);
  const topVisited = useMemo(() => {
    if (loading)
      return (
        <>
          <Loader />
          <Loader />
        </>
      );
    return result?.value?.results && topByCount?.length ? (
      (() => {
        const filteredItems = topByCount
          .map(el => {
            const entity = result?.value?.results.find(
              e => e.document.id === el,
            );
            return entity ? (
              <SearchFavoriteListItem key={el} entity={entity} />
            ) : null;
          })
          .filter(Boolean);

        return filteredItems.length > 0 ? (
          filteredItems
            .filter(el => el !== null)
            .slice(0, 5)
            .map((el, i) => {
              return (
                el && (
                  <Fragment key={`${el.key}-topVisited`}>
                    {el}
                    {i < filteredItems.length - 1 && i < 4 && (
                      <WbDivider orientation="horizontal" />
                    )}
                  </Fragment>
                )
              );
            })
        ) : (
          <HomepageEmptyState kind="top" />
        );
      })()
    ) : (
      <HomepageEmptyState kind="top" />
    );
  }, [loading, result?.value?.results, topByCount]);
  const favoriteItems = useMemo(() => {
    if (result.loading)
      return (
        <>
          <Loader />
          <Loader />
        </>
      );
    return result?.value?.results && favorites.length ? (
      (() => {
        const filteredItems = favorites
          .map(el => {
            const entity = result?.value?.results.find(
              e => e.document.id === el,
            );
            return entity ? (
              <SearchFavoriteListItem key={el} entity={entity} />
            ) : null;
          })
          .filter(Boolean);

        return filteredItems.length > 0 ? (
          filteredItems
            .filter(el => el !== null)
            .slice(0, 5)
            .map((el, i) => {
              return (
                el && (
                  <Fragment key={`${el.key}-favorite`}>
                    {el}
                    {i < filteredItems.length - 1 && i < 4 && (
                      <WbDivider orientation="horizontal" />
                    )}
                  </Fragment>
                )
              );
            })
        ) : (
          <HomepageEmptyState kind="favorites" />
        );
      })()
    ) : (
      <HomepageEmptyState kind="favorites" />
    );
  }, [result.loading, result?.value?.results, favorites]);
  if (result.error || metrics.error)
    return <ErrorPanel error={(result.error || metrics.error) as Error} />;

  return (
    <Box className={classes.container}>
      {!isMobile && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            display: 'flex',
            height: 32,
          }}
        >
          <WbSupportButton />
          {features?.wittyAssistantEnabled && <WittyAssistant />}
        </div>
      )}
      <Box className={classes.logoContainer}>
        <Logo showDemoBadge={showDemoBadge} />
      </Box>
      <Typography variant="h4" className={classes.title}>
        <label htmlFor={searchInputId}>Marketplace Search</label>
      </Typography>
      <form onSubmit={handleSubmit} className={classes.form}>
        <SearchPageAutocomplete
          id={searchInputId}
          query={activeQuery}
          mode={mode}
          semanticAvailable={semanticAvailable}
          onModeChange={nextMode => {
            setQuery('');
            setMode(nextMode);
          }}
          onChange={nextQuery => {
            if (!semanticAvailable && preferredMode === 'semantic')
              setMode('keyword');
            setQuery(nextQuery);
          }}
          handleSubmit={handleSubmit}
        />
      </form>
      <Box className={classes.gridContainer}>
        <WbCard
          leading={
            <BarChartIcon
              style={{ marginLeft: 12, color: theme.palette.secondary.main }}
            />
          }
          title="Top Visited"
          cardClassName={classes.widget}
        >
          <Box style={{ padding: '0.5rem', flexGrow: 1 }}>{topVisited}</Box>
        </WbCard>
        <WbCard
          leading={
            <HistoryIcon
              style={{ marginLeft: 12, color: theme.palette.secondary.main }}
            />
          }
          title="Recently Visited"
          cardClassName={classes.widget}
        >
          <Box style={{ padding: '0.5rem', flexGrow: 1 }}>
            {recentlyVisited}
          </Box>
        </WbCard>
        <WbCard
          leading={
            <HotelClassIcon
              style={{ marginLeft: 12, color: theme.palette.secondary.main }}
            />
          }
          title="Your Favorites"
          cardClassName={classes.widget}
        >
          <Box style={{ padding: '0.5rem', flexGrow: 1 }}>{favoriteItems}</Box>
        </WbCard>
      </Box>
    </Box>
  );
};
